import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiDownload, FiMonitor } from 'react-icons/fi';
import useDesktopPwaStatus from '../../hooks/useDesktopPwaStatus';
import {
  PWA_INSTALL_HINT,
  PRODUCTION_PWA_ORIGIN,
  getProductionPwaUrl,
  promptPwaInstall,
  shouldOfferPwaInstall,
} from '../../utils/pwa';

/**
 * Mismo CTA de instalación en landing (hero) y Cuenta.
 * variant: hero | onBlue | account
 */
const DesktopAppInstallCta = ({ variant = 'hero', modoOscuro = false, className = '' }) => {
  const { loading, kind, canPrompt, desktop, refresh } = useDesktopPwaStatus();
  const [hintOpen, setHintOpen] = useState(false);

  if (loading) {
    if (variant === 'account') {
      return <p className={`text-sm ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>Comprobando instalación…</p>;
    }
    return null;
  }

  if (kind === 'local-dev' || kind === 'local-dev-installed') {
    const prodUrl = getProductionPwaUrl('/login');
    const textClass = modoOscuro ? 'text-gray-300' : 'text-gray-600';
    return (
      <div className={className}>
        <p className={`text-sm leading-relaxed ${textClass}`}>
          {kind === 'local-dev-installed' ? (
            <>
              <strong className={modoOscuro ? 'text-white' : 'text-gray-900'}>
                Esta ventana es la app instalada desde localhost.
              </strong>
              {' '}Solo funciona con el front y el backend en tu PC. Desinstalá Contsuite en Windows e instalá desde{' '}
              <a href={prodUrl} className="font-semibold text-[#1F80FF] hover:underline">
                {PRODUCTION_PWA_ORIGIN}
              </a>
              .
            </>
          ) : (
            <>
              En desarrollo local no conviene instalar la PWA: el ícono del escritorio quedará atado a{' '}
              <code className="text-xs">localhost</code>. Abrí{' '}
              <a href={prodUrl} className="font-semibold text-[#1F80FF] hover:underline">
                {PRODUCTION_PWA_ORIGIN}
              </a>{' '}
              en Chrome o Edge e instalá desde ahí.
            </>
          )}
        </p>
      </div>
    );
  }

  if (!desktop) {
    if (variant === 'account') {
      return (
        <p className={`text-sm leading-relaxed ${modoOscuro ? 'text-gray-300' : 'text-gray-600'}`}>
          Para instalar en tu PC, abrí Contsuite en Chrome o Edge desde una computadora.
        </p>
      );
    }
    return null;
  }

  const onInstallClick = async () => {
    if (kind !== 'not-installed') return;
    if (!shouldOfferPwaInstall()) {
      setHintOpen(true);
      return;
    }
    if (canPrompt) {
      const result = await promptPwaInstall();
      if (result.outcome === 'accepted') await refresh();
      return;
    }
    setHintOpen((v) => !v);
  };

  if (kind === 'running-app') {
    const linkClass =
      variant === 'onBlue'
        ? 'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border border-white/80 text-white hover:bg-white hover:text-[#004aad] transition-colors'
        : variant === 'account'
          ? 'inline-flex items-center gap-2 rounded-lg bg-[#1F80FF] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1a6fe0]'
          : `inline-flex items-center gap-2 text-sm font-semibold underline-offset-2 hover:underline ${
              modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'
            }`;

    return (
      <div className={`${variant === 'hero' ? 'pt-2' : variant === 'onBlue' ? 'mt-4' : ''} ${className}`}>
        {variant === 'account' && (
          <p className={`text-sm mb-2 ${modoOscuro ? 'text-gray-300' : 'text-gray-600'}`}>
            Estás en la <strong className={modoOscuro ? 'text-white' : 'text-gray-900'}>app de escritorio</strong> instalada.
          </p>
        )}
        <Link to="/inicio" className={linkClass}>
          <FiMonitor className="w-4 h-4" aria-hidden />
          {variant === 'account' ? 'Ir al panel' : 'Abrir Contsuite instalada'}
        </Link>
      </div>
    );
  }

  if (kind === 'installed') {
    return (
      <div className={`${variant === 'hero' ? 'pt-2' : variant === 'onBlue' ? 'mt-4' : ''} ${className}`}>
        <p
          className={`text-sm leading-relaxed ${
            variant === 'onBlue'
              ? 'text-white/90 text-center max-w-md mx-auto'
              : modoOscuro
                ? 'text-gray-300'
                : 'text-gray-600'
          }`}
        >
          <strong className={variant === 'onBlue' ? 'text-white' : modoOscuro ? 'text-white' : 'text-gray-900'}>
            Ya está instalada en esta computadora.
          </strong>
          {variant !== 'hero' && ' Abrila desde el menú Inicio o el escritorio.'}
        </p>
        {variant === 'account' && (
          <Link
            to="/inicio"
            className={`mt-3 inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-xs font-semibold ${
              modoOscuro ? 'border-gray-600 text-gray-200 hover:bg-gray-800' : 'border-gray-300 text-gray-800 hover:bg-gray-50'
            }`}
          >
            Seguir en el navegador
          </Link>
        )}
      </div>
    );
  }

  const baseBtn =
    variant === 'onBlue'
      ? 'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border border-white/80 text-white hover:bg-white hover:text-[#004aad] transition-colors'
      : variant === 'account'
        ? 'inline-flex items-center justify-center gap-2 rounded-lg bg-[#1F80FF] px-4 py-2.5 text-xs font-semibold text-white hover:bg-[#1a6fe0]'
        : `inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
            modoOscuro
              ? 'border-white/40 text-white hover:bg-white/10'
              : 'border-black/20 text-gray-900 hover:bg-black/5'
          }`;

  const hintClass =
    variant === 'onBlue'
      ? 'text-xs text-white/85 mt-2 max-w-md mx-auto text-center'
      : `text-xs mt-2 max-w-md ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`;

  const wrapClass =
    variant === 'hero'
      ? 'pt-2 flex flex-col items-center lg:items-start'
      : variant === 'onBlue'
        ? 'mt-4'
        : '';

  return (
    <div className={`${wrapClass} ${className}`}>
      {variant === 'account' && (
        <p className={`text-sm mb-3 ${modoOscuro ? 'text-gray-300' : 'text-gray-600'}`}>
          Mismo acceso que en la landing: un clic y queda en tu PC como programa.
        </p>
      )}
      <button type="button" onClick={onInstallClick} className={baseBtn}>
        <FiDownload className="w-4 h-4" aria-hidden />
        {canPrompt ? 'Descargar app de escritorio' : 'Cómo instalar en tu PC'}
      </button>
      {hintOpen && (
        <p className={hintClass} role="note">
          {PWA_INSTALL_HINT}
          {!canPrompt && variant !== 'account' && (
            <>
              {' '}
              Si no ves el ícono,{' '}
              <Link
                to="/login"
                className={`font-semibold underline underline-offset-2 ${
                  variant === 'onBlue' ? 'text-white' : modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'
                }`}
              >
                iniciá sesión
              </Link>{' '}
              y volvé a intentar.
            </>
          )}
        </p>
      )}
    </div>
  );
};

export default DesktopAppInstallCta;
